import { formatDate, formatShortDate, formatTime } from '@/lib/formatDate';
import { useEffect, useState, useCallback, useRef, useMemo } from "react"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { getRecentPatients, getUpcomingAppointments, searchPatients } from "@/lib/api"
import { displayAge } from "@/lib/patientAge"
import { useNavigate } from "react-router-dom"
import { useAuth } from "@/context/AuthContext"
import { useTheme } from "@/context/ThemeContext"
import { usePermission } from "@/hooks/usePermission"
import { useNavItems } from "@/components/Sidebar"
import { OPEN_HELP_EVENT } from "@/components/HelpPanel"
import AddAppointmentModal from "@/components/Appointment/AddAppointmentModal"
import NewPatientModal from "@/components/Patient/NewPatientModal"
import {
  Search, User, Calendar, CalendarPlus, FilePlus2, UserPlus, Loader2, AlertCircle,
  ArrowRight, HelpCircle, Moon, Sun, type LucideIcon,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useTipPrefs } from "@/hooks/useTipPrefs"

// Command palette (Ctrl/⌘+K): one keyboard list of commands, patients and today's appointments.
// "rx <name>" jumps straight to a new prescription for that patient; "book <name>" books them.

type Props = { open: boolean; onClose: () => void; initialQuery?: string }

/** Opens search from anywhere; `detail` is the starting query (e.g. RX_PICK_QUERY to choose a patient for a new Rx) */
export const OPEN_SEARCH_EVENT = 'open-search'
export const RX_PICK_QUERY = 'New Rx for '
// "New Rx for " with no name yet: list recent patients, each starting a prescription
const RX_PICK_RE = /^(?:new\s+)?(?:rx|prescription)\s+(?:for\s*)?$/i

type Row = {
  key: string
  icon: LucideIcon
  label: string
  detail?: string
  hint?: string
  run: () => void
  /** Patient rows offer quick actions next to the name */
  patient?: { id: string; name: string }
}
type Group = { title: string; rows: Row[] }

type Intent = 'rx' | 'book' | null
const INTENT_RE: [RegExp, Exclude<Intent, null>][] = [
  [/^(?:new\s+)?(?:rx|prescription)\s+(?:for\s+)?(.+)$/i, 'rx'],
  [/^(?:book|appointment|appt)\s+(.+)$/i, 'book'],
]

function parseIntent(q: string): { intent: Intent; term: string } {
  const t = q.trim()
  for (const [re, intent] of INTENT_RE) {
    const m = t.match(re)
    if (m) return { intent, term: m[1].trim() }
  }
  return { intent: null, term: t }
}

/** Every word of the query appears in the text (case-insensitive) */
const matches = (text: string, q: string) => {
  const hay = text.toLowerCase()
  return q.toLowerCase().split(/\s+/).filter(Boolean).every(w => hay.includes(w))
}

function patientDetail(p: any) {
  return [displayAge(p.age, p.date_of_birth) || null, p.gender || null, p.phone || null,
    p.last_visit ? `Last seen ${formatDate(p.last_visit)}` : null].filter(Boolean).join(' · ')
}

export default function SearchOverlay({ open, onClose, initialQuery }: Props) {
  const { currentUser } = useAuth()
  const { can } = usePermission()
  const { theme, setTheme } = useTheme()
  const navItems = useNavItems()
  const navigate = useNavigate()
  const { markSeen } = useTipPrefs()

  const isFrontDesk = !currentUser?.doctor_id
  const canViewAppointments = can('appointments:read')
  const canManageAppointments = can('appointments:write')
  const canViewPatients = can('patients:read')
  const canManagePatients = can('patients:write')
  const canPrescribe = !isFrontDesk && can('prescriptions:write')

  const [query, setQuery] = useState("")
  const [found, setFound] = useState<any[]>([])
  const [recentPatients, setRecentPatients] = useState<any[]>([])
  const [upcoming, setUpcoming] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState(0)
  const [showNewPatient, setShowNewPatient] = useState(false)
  const [newPatientSearch, setNewPatientSearch] = useState('')
  const [bookFor, setBookFor] = useState<{ open: boolean; patient?: { id: string; name: string } }>({ open: false })
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const { intent: parsedIntent, term } = parseIntent(query)
  const rxPick = RX_PICK_RE.test(query)
  const intent: Intent = rxPick ? 'rx' : parsedIntent
  const patientPath = (id: string) => (isFrontDesk ? `/frontdesk/patients/${id}` : `/patients/${id}`)

  // ── Data ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      setQuery(""); setFound([]); setError(null); setSelected(0)
      return
    }
    if (initialQuery) setQuery(initialQuery)
    setTimeout(() => inputRef.current?.focus(), 50)
    markSeen('quick-search') // the Home tips card needn't suggest search any more
    if (!currentUser?.clinic_id) return
    Promise.all([
      canViewPatients ? getRecentPatients(5, currentUser.doctor_id ?? undefined, currentUser.clinic_id) : Promise.resolve([]),
      canViewAppointments ? getUpcomingAppointments(5, currentUser.doctor_id ?? undefined, currentUser.clinic_id) : Promise.resolve([]),
    ])
      .then(([p, a]) => { setRecentPatients(Array.isArray(p) ? p : []); setUpcoming(a || []) })
      .catch(e => console.error("Failed to load command palette defaults:", e))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentUser?.doctor_id, currentUser?.clinic_id, canViewPatients, canViewAppointments])

  useEffect(() => {
    if (!open || !canViewPatients || term.length < 2) { setFound([]); setLoading(false); return }
    setLoading(true); setError(null)
    const id = setTimeout(async () => {
      try {
        const r = await searchPatients(term, currentUser?.clinic_id)
        setFound((r || []).slice(0, 8))
      } catch (err) {
        console.error("Search failed:", err)
        setError("Search failed. Please check your connection.")
        setFound([])
      } finally {
        setLoading(false)
      }
    }, 250)
    return () => clearTimeout(id)
  }, [term, open, canViewPatients, currentUser?.clinic_id])

  // ── Actions ─────────────────────────────────────────────────────────────────
  const close = onClose
  const openPatient = useCallback((id: string) => { navigate(patientPath(id)); close() }, [navigate, close, isFrontDesk])
  const newRxFor = useCallback((id: string) => { navigate(`/patients/${id}?openPrescription=true`); close() }, [navigate, close])
  const bookPatient = useCallback((p?: { id: string; name: string }) => { close(); setBookFor({ open: true, patient: p }) }, [close])

  const commands: (Row & { keywords: string })[] = useMemo(() => {
    const list: (Row & { keywords: string })[] = []
    if (canManagePatients) list.push({ key: 'new-patient', icon: UserPlus, label: 'New patient', hint: 'Ctrl N', keywords: 'add register', run: () => { close(); setNewPatientSearch(''); setShowNewPatient(true) } })
    if (canManageAppointments) list.push({ key: 'book', icon: CalendarPlus, label: 'Book an appointment', keywords: 'appointment schedule', run: () => bookPatient() })
    if (canPrescribe) list.push({ key: 'blank-rx', icon: FilePlus2, label: 'New blank prescription', keywords: 'rx prescription write', run: () => { setQuery(RX_PICK_QUERY); inputRef.current?.focus() } })
    for (const n of navItems) {
      list.push({ key: `go-${n.to}`, icon: n.icon as LucideIcon, label: `Go to ${n.label}`, keywords: `open page ${n.label}`, run: () => { navigate(n.to); close() } })
    }
    list.push({ key: 'help', icon: HelpCircle, label: 'Help and shortcuts', hint: '?', keywords: 'tips keyboard', run: () => { close(); window.dispatchEvent(new Event(OPEN_HELP_EVENT)) } })
    const dark = theme === 'dark'
    list.push({ key: 'theme', icon: dark ? Sun : Moon, label: dark ? 'Switch to light theme' : 'Switch to dark theme', keywords: 'theme mode appearance dark light night', run: () => { setTheme(dark ? 'light' : 'dark'); close() } })
    return list
  }, [canManagePatients, canManageAppointments, canPrescribe, navItems, theme, setTheme, navigate, close, bookPatient])

  // ── What to show ────────────────────────────────────────────────────────────
  const groups: Group[] = useMemo(() => {
    const patientRow = (p: any): Row => {
      const base = { key: `p-${p.id}`, icon: User, detail: patientDetail(p), patient: { id: p.id, name: p.name || '—' } }
      if (intent === 'rx') return { ...base, label: `New prescription for ${p.name}`, run: () => newRxFor(p.id) }
      if (intent === 'book') return { ...base, label: `Book ${p.name}`, run: () => bookPatient({ id: p.id, name: p.name }) }
      return { ...base, label: p.name || '—', run: () => openPatient(p.id) }
    }

    if (!query.trim()) {
      const out: Group[] = []
      const suggested = commands.filter(c => ['new-patient', 'book', 'blank-rx'].includes(c.key))
      if (suggested.length) out.push({ title: 'Actions', rows: suggested })
      if (canViewAppointments && upcoming.length) out.push({
        title: 'Upcoming appointments',
        rows: upcoming.map(a => ({
          key: `a-${a.id}`, icon: Calendar, label: a.patient_name,
          detail: [formatShortDate(a.date), a.time ? formatTime(`1970-01-01T${a.time}`) : null, a.reason || null].filter(Boolean).join(' · '),
          run: () => openPatient(a.patient_id), patient: { id: a.patient_id, name: a.patient_name },
        })),
      })
      if (canViewPatients && recentPatients.length) out.push({ title: 'Recent patients', rows: recentPatients.map(patientRow) })
      return out
    }

    const out: Group[] = []
    if (intent === 'rx' && !canPrescribe) return out
    if (rxPick) {
      if (canViewPatients && recentPatients.length) out.push({ title: 'New prescription for… (recent patients, or type a name)', rows: recentPatients.map(patientRow) })
      return out
    }
    if (!intent) {
      const cmds = commands.filter(c => matches(`${c.label} ${c.keywords}`, term)).slice(0, 6)
      if (cmds.length) out.push({ title: 'Commands', rows: cmds })
    }
    if (found.length) out.push({ title: intent === 'rx' ? 'New prescription for…' : intent === 'book' ? 'Book appointment for…' : 'Patients', rows: found.map(patientRow) })
    return out
  }, [query, term, intent, rxPick, commands, found, upcoming, recentPatients, canViewAppointments, canViewPatients, canPrescribe, newRxFor, bookPatient, openPatient])

  const rows = useMemo(() => groups.flatMap(g => g.rows), [groups])
  useEffect(() => { setSelected(0) }, [query, rows.length])

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${selected}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [selected])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!rows.length) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(i => (i + 1) % rows.length) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelected(i => (i - 1 + rows.length) % rows.length) }
    else if (e.key === 'Enter') { e.preventDefault(); rows[selected]?.run() }
  }

  let index = -1
  const showNoResults = query.trim().length > 0 && !loading && !error && rows.length === 0 && term.length >= 2

  return (
    <>
      <Dialog open={open} onOpenChange={o => { if (!o) close() }}>
        <DialogContent className="max-w-2xl p-0 gap-0 max-h-[70vh] overflow-hidden flex flex-col top-[15vh] translate-y-0">
          <DialogTitle className="sr-only">Search or run a command</DialogTitle>

          <div className="flex items-center gap-3 h-14 pl-4 pr-12 border-b border-border flex-shrink-0">
            <Search className="w-5 h-5 text-muted-foreground flex-shrink-0" aria-hidden />
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search patients or type a command…"
              aria-label="Search patients or type a command"
              role="combobox"
              aria-expanded
              aria-controls="command-list"
              aria-activedescendant={rows.length ? `cmd-${selected}` : undefined}
              className="flex-1 h-full bg-transparent text-base outline-none placeholder:text-muted-foreground"
            />
            {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" aria-label="Searching" />}
            {intent && (
              <span className="text-xs font-medium px-2 py-0.5 rounded bg-primary/10 text-primary whitespace-nowrap">
                {intent === 'rx' ? 'New prescription' : 'Book appointment'}
              </span>
            )}
          </div>

          <div ref={listRef} id="command-list" role="listbox" aria-label="Results" className="flex-1 overflow-y-auto min-h-0 p-2">
            {error && (
              <div className="m-2 flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
              </div>
            )}

            {groups.map(g => (
              <div key={g.title} className="mb-2 last:mb-0" role="group" aria-label={g.title}>
                <div className="px-3 pt-2 pb-1 text-xs font-medium text-muted-foreground">{g.title}</div>
                {g.rows.map(r => {
                  index += 1
                  const i = index
                  const active = i === selected
                  const Icon = r.icon
                  return (
                    <div
                      key={r.key}
                      id={`cmd-${i}`}
                      data-index={i}
                      role="option"
                      aria-selected={active}
                      onMouseMove={() => setSelected(i)}
                      onClick={r.run}
                      className={cn(
                        "flex items-center gap-3 min-h-10 px-3 py-1.5 rounded-md cursor-pointer",
                        active ? "bg-accent text-accent-foreground" : "text-foreground"
                      )}
                    >
                      <Icon className={cn("w-4 h-4 flex-shrink-0", active ? "text-accent-foreground" : "text-muted-foreground")} aria-hidden />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{r.label}</div>
                        {r.detail && <div className="text-xs text-muted-foreground truncate">{r.detail}</div>}
                      </div>
                      {r.patient && !intent && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {canPrescribe && (
                            <button type="button" onClick={e => { e.stopPropagation(); newRxFor(r.patient!.id) }}
                                    className="h-7 px-2 rounded text-xs font-medium text-muted-foreground hover:bg-card hover:text-foreground border border-transparent hover:border-border">
                              New Rx
                            </button>
                          )}
                          {canManageAppointments && (
                            <button type="button" onClick={e => { e.stopPropagation(); bookPatient(r.patient) }}
                                    className="h-7 px-2 rounded text-xs font-medium text-muted-foreground hover:bg-card hover:text-foreground border border-transparent hover:border-border">
                              Book
                            </button>
                          )}
                        </div>
                      )}
                      {r.hint && <kbd className="text-2xs font-mono text-muted-foreground border border-border rounded px-1.5 py-0.5 bg-card">{r.hint}</kbd>}
                      {active && !r.hint && !r.patient && <ArrowRight className="w-4 h-4 flex-shrink-0" aria-hidden />}
                    </div>
                  )
                })}
              </div>
            ))}

            {showNoResults && (
              <div className="flex flex-col items-center justify-center py-10 text-center gap-3">
                <p className="text-sm text-muted-foreground">No patients or commands match “{term}”.</p>
                {canManagePatients && (
                  <button type="button" onClick={() => { close(); setNewPatientSearch(term); setShowNewPatient(true) }}
                          className="inline-flex items-center gap-2 h-8 px-3 rounded-md text-sm font-medium border border-border hover:bg-muted">
                    <UserPlus className="w-4 h-4" /> Add a new patient
                  </button>
                )}
              </div>
            )}

            {query.trim().length > 0 && term.length < 2 && !rows.length && (
              <p className="px-3 py-6 text-sm text-muted-foreground text-center">Keep typing to search patients…</p>
            )}
          </div>

          <div className="flex items-center justify-between gap-4 px-4 h-10 border-t border-border bg-input text-xs text-muted-foreground flex-shrink-0">
            <div className="flex items-center gap-3">
              <span><kbd className="font-mono">↑↓</kbd> move</span>
              <span><kbd className="font-mono">↵</kbd> open</span>
              <span><kbd className="font-mono">Esc</kbd> close</span>
            </div>
            <span className="truncate">
              Try <span className="font-mono text-foreground">{canPrescribe ? 'rx om' : 'book om'}</span>
              {canPrescribe && canManageAppointments && <> or <span className="font-mono text-foreground">book om</span></>}
            </span>
          </div>
        </DialogContent>
      </Dialog>

      <NewPatientModal
        isOpen={showNewPatient}
        onClose={() => setShowNewPatient(false)}
        onPatientCreated={() => setShowNewPatient(false)}
        title="New patient"
        initialSearch={newPatientSearch}
      />

      <AddAppointmentModal
        isOpen={bookFor.open}
        onClose={() => setBookFor({ open: false })}
        patientId={bookFor.patient?.id ?? ''}
        patients={(bookFor.patient ? [bookFor.patient] : recentPatients) as any}
        onAdded={() => setBookFor({ open: false })}
        showPatientSelection={true}
      />
    </>
  )
}
